import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, ArrowLeft, Plus, Search, FileDown, BarChart3, Calendar, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import FormularioGestion from './FormularioGestion';
import AccionesGestion from './AccionesGestion';
import DetalleGestion from './DetalleGestion';
import EditarGestion from './EditarGestion';
import EstadisticasModulo from './EstadisticasModulo';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModuloDenuncias = ({ onBack }) => {
  const { user, canCreateCases, canEditCases, isAnalyst, canViewGestion } = useAuth();
  const [view, setView] = useState('list');
  const [gestiones, setGestiones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGestion, setSelectedGestion] = useState(null);
  
  // Catálogos para filtros
  const [instituciones, setInstituciones] = useState([]);
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  
  const [filtros, setFiltros] = useState({
    q: '',
    estado: '',
    institucion: '',
    asesor_cnc: '',
    fecha_asignacion_desde: '',
    fecha_asignacion_hasta: '',
    fecha_vencimiento_cnc_desde: '',
    fecha_vencimiento_cnc_hasta: '',
    fecha_vencimiento_probidad_desde: '',
    fecha_vencimiento_probidad_hasta: ''
  });

  useEffect(() => {
    fetchGestiones();
    fetchCatalogos();
  }, []);
  
  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [instResponse, asesoresResponse] = await Promise.all([
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogos/asesores-cnc?activo=true`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setInstituciones(instResponse.data || []);
      setAsesoresCNC(asesoresResponse.data || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      // Buscar con ambos nombres para compatibilidad
      const [response1, response2] = await Promise.all([
        axios.get(`${API}/gestiones?tipo_gestion=Dirección Ejecutiva`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Gestión de Información`).catch(() => ({ data: [] }))
      ]);
      let allGestiones = [...(response1.data || []), ...(response2.data || [])];
      let uniqueGestiones = allGestiones.filter((g, index, self) => 
        index === self.findIndex(t => t.id === g.id)
      );
      // Si es analista, filtrar solo sus gestiones
      if (isAnalyst()) {
        uniqueGestiones = uniqueGestiones.filter(g => canViewGestion(g));
      }
      setGestiones(uniqueGestiones);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar las gestiones');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = () => {
    if (gestiones.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = gestiones.map(g => ({
      'Nomenclatura': g.nomenclatura,
      'Fecha': new Date(g.fecha_asignacion).toLocaleDateString('es-ES'),
      'Tipo Información': g.campos_especificos?.tipo_informacion || 'N/A',
      'Canal Recepción': g.campos_especificos?.canal_recepcion || 'N/A',
      'Solicitante': g.campos_especificos?.solicitante || 'N/A',
      'Institución': g.institucion,
      'Estado': g.estado,
      'Asesor CNC': g.nombre_asesor_cnc,
      'Descripción': g.descripcion
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gestión Información');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Gestion_Informacion_${fecha}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const handleBuscar = () => {
    fetchGestiones();
    toast.success('Búsqueda actualizada');
  };

  const handleViewDetail = (gestion) => {
    setSelectedGestion(gestion);
    setView('detail');
  };

  const handleEditGestion = (gestion) => {
    setSelectedGestion(gestion);
    setView('edit');
  };

  const renderCamposEspecificos = (formData, handleChange) => (
    <div className="form-grid">
      <div className="form-field">
        <label className="required">Fecha recepción</label>
        <input
          type="date"
          value={formData.campos_especificos.fecha_recepcion || ''}
          onChange={(e) => handleChange('fecha_recepcion', e.target.value)}
          required
        />
      </div>

      <div className="form-field">
        <label className="required">Canal de Recepción</label>
        <select
          value={formData.campos_especificos.canal_recepcion || ''}
          onChange={(e) => handleChange('canal_recepcion', e.target.value)}
          required
        >
          <option value="">Seleccione canal</option>
          <option value="Telefónica">Telefónica</option>
          <option value="Escrita">Escrita</option>
          <option value="Reunión">Reunión</option>
          <option value="Correo electrónico">Correo electrónico</option>
          <option value="Presencial">Presencial</option>
          <option value="Otro">Otro</option>
        </select>
      </div>
    </div>
  );

  // Mostrar vista de detalle
  if (view === 'detail' && selectedGestion) {
    return (
      <div className="modulo-container">
        <DetalleGestion 
          gestionId={selectedGestion.id} 
          onBack={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
          }} 
        />
      </div>
    );
  }

  // Mostrar vista de edición
  if (view === 'edit' && selectedGestion) {
    return (
      <div className="modulo-container">
        <EditarGestion 
          gestion={selectedGestion}
          onSuccess={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
            toast.success('Gestión actualizada correctamente');
          }}
          onCancel={() => {
            setSelectedGestion(null);
            setView('list');
          }}
        />
      </div>
    );
  }

  return (
    <div className="modulo-container">
      <div className="modulo-header-bar">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Volver a Módulos
        </button>
        
        <div className="modulo-title-section">
          <div className="modulo-icon-header" style={{ backgroundColor: '#FF9800' }}>
            <FileText size={24} />
          </div>
          <div>
            <h1 className="modulo-main-title">Dirección Ejecutiva</h1>
            <p className="modulo-nomenclatura">Nomenclatura: DE-###</p>
          </div>
        </div>
      </div>

      <div className="modulo-tabs">
        <button 
          className={`modulo-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Listado de Gestiones
        </button>
        {canCreateCases() && (
          <button 
            className={`modulo-tab ${view === 'create' ? 'active' : ''}`}
            onClick={() => setView('create')}
          >
            <Plus size={16} />
            Nueva Gestión
          </button>
        )}
        <button 
          className={`modulo-tab ${view === 'stats' ? 'active' : ''}`}
          onClick={() => setView('stats')}
        >
          <BarChart3 size={16} />
          Estadísticas
        </button>
      </div>

      <div className="modulo-content">
        {view === 'list' && (
          <div>
            {/* Filtros */}
            <div className="panel filters-compact" style={{ marginBottom: '1.5rem' }}>
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '1rem' }}>
                <div className="filter-item">
                  <label>Búsqueda</label>
                  <input
                    type="text"
                    placeholder="Nomenclatura, solicitante, institución..."
                    value={filtros.q}
                    onChange={(e) => setFiltros({...filtros, q: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Estado</label>
                  <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})}>
                    <option value="">Todos</option>
                    <option value="Asignada">Asignada</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Completada">Completada</option>
                  </select>
                </div>
                <div className="filter-item">
                  <label>Desde</label>
                  <input type="date" value={filtros.fecha_desde} onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Hasta</label>
                  <input type="date" value={filtros.fecha_hasta} onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})} />
                </div>
                <button className="btn btn-primary" style={{ alignSelf: 'flex-end' }} onClick={handleBuscar}>
                  <Search size={16} />
                  Buscar
                </button>
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                <h2>Gestiones de Dirección Ejecutiva ({gestiones.length})</h2>
                <button className="btn btn-secondary" onClick={handleExportarExcel}>
                  <FileDown size={16} />
                  Exportar Excel
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="loader"></div>
                </div>
              ) : gestiones.length === 0 ? (
                <div className="empty-state">
                  <FileText size={48} style={{ opacity: 0.3 }} />
                  <p>No hay gestiones de Dirección Ejecutiva registradas</p>
                  {canCreateCases() && (
                    <button className="btn btn-primary" onClick={() => setView('create')}>
                      <Plus size={16} />
                      Registrar Primera Gestión
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Nomenclatura</th>
                        <th>Fecha</th>
                        <th>Canal</th>
                        <th>Institución</th>
                        <th>Estado</th>
                        <th>Asesor CNC</th>
                        <th>Venc. CNC</th>
                        <th>Probidad</th>
                        <th>Asesor IP</th>
                        <th>Venc. IP</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestiones.map((gestion) => {
                        const hoy = new Date();
                        const fechaVencCNC = gestion.fecha_vencimiento ? new Date(gestion.fecha_vencimiento) : null;
                        const fechaVencIP = gestion.fecha_vencimiento_probidad ? new Date(gestion.fecha_vencimiento_probidad) : null;
                        const vencidoCNC = fechaVencCNC && fechaVencCNC < hoy && gestion.estado !== 'Completada / Archivada';
                        const vencidoIP = fechaVencIP && fechaVencIP < hoy && gestion.estado !== 'Completada / Archivada';
                        
                        return (
                          <tr key={gestion.id}>
                            <td><strong>{gestion.nomenclatura}</strong></td>
                            <td>{new Date(gestion.fecha_asignacion).toLocaleDateString('es-ES')}</td>
                            <td>
                              <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                                {gestion.campos_especificos?.canal_recepcion || 'N/A'}
                              </span>
                            </td>
                            <td>{gestion.institucion}</td>
                            <td>
                              <span className={`badge ${gestion.estado === 'Completada / Archivada' ? 'badge-success' : gestion.estado === 'En proceso de análisis' ? 'badge-info' : 'badge-warning'}`}>
                                {gestion.estado}
                              </span>
                            </td>
                            <td>{gestion.nombre_asesor_cnc}</td>
                            <td style={{ color: vencidoCNC ? '#D32F2F' : 'inherit', fontWeight: vencidoCNC ? 600 : 'normal' }}>
                              {fechaVencCNC ? fechaVencCNC.toLocaleDateString('es-ES') : '-'}
                              {vencidoCNC && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                            </td>
                            <td>
                              {gestion.asesor_probidad_id ? (
                                <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>Sí</span>
                              ) : (
                                <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>No</span>
                              )}
                            </td>
                            <td style={{ fontSize: '0.8rem' }}>{gestion.asesor_probidad_nombre || '-'}</td>
                            <td style={{ color: vencidoIP ? '#D32F2F' : 'inherit', fontWeight: vencidoIP ? 600 : 'normal' }}>
                              {fechaVencIP ? fechaVencIP.toLocaleDateString('es-ES') : '-'}
                              {vencidoIP && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                            </td>
                            <td>
                              <AccionesGestion 
                                gestion={gestion}
                                onView={handleViewDetail}
                                onEdit={canEditCases() ? handleEditGestion : null}
                                compact={true}
                                canEdit={canEditCases()}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'create' && (
          <FormularioGestion
            tipoGestion="Dirección Ejecutiva"
            nomenclatura="DE"
            camposEspecificos={renderCamposEspecificos}
            onSuccess={() => {
              fetchGestiones();
              setView('list');
            }}
            onCancel={() => setView('list')}
          />
        )}

        {view === 'stats' && (
          <EstadisticasModulo 
            tipoGestion="Dirección Ejecutiva" 
            colorModulo="#FF9800"
          />
        )}
      </div>
    </div>
  );
};

export default ModuloDenuncias;
